import { CAP_WINDOW_MS, type CountedText } from '@fundline/shared/compliance/caps';
import {
  describeLocationSource,
  formatLocalTimeOfDay as formatTimeOfDay,
  formatClock,
  formatDayTime,
  formatInZones,
  zoneAbbreviation,
  zoneName,
} from '@fundline/shared/compliance/display';
import type { ComplianceData, ComplianceDecision } from '@fundline/shared/compliance/evaluate';
import { resolveLocation } from '@fundline/shared/compliance/location';
import { assembleRules, type OverrideRow } from '@fundline/shared/compliance/rules';
import type { MessageClass } from '@fundline/shared/compliance/window';
import { type ReactNode, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField } from '../../components/ui/Field.tsx';
import { Spinner } from '../../components/ui/Layout.tsx';
import {
  type MerchantForRules,
  previewDecision,
  useComplianceReferenceQuery,
  useNow,
  useSetMerchantTimezone,
  useStateOverridesQuery,
} from './use-sending-rules.ts';

/**
 * Sending-hours previews (M7, brand/layout.md: the merchant's local time in the conversation header and
 * the details panel, a quiet-hours notice in the composer). Previews only: the worker's final gate
 * enforces the same shared rules and has the last word.
 */

export interface RulesState {
  data: ComplianceData | undefined;
  overrides: OverrideRow[] | undefined;
  error: Error | null;
}

export function useRules(workspaceId: string): RulesState {
  const reference = useComplianceReferenceQuery();
  const overrides = useStateOverridesQuery(workspaceId);
  return {
    data: reference.data,
    overrides: overrides.data,
    error: reference.error ?? overrides.error ?? null,
  };
}

const COUNTED = new Set(['sending', 'sent', 'delivered', 'failed', 'unknown']);
const REQUIRED = new Set(['opt_out_confirmation', 'help_reply', 'resubscribe_confirmation']);

/** The thread's texts that count toward a per-24 h cap, the way the worker counts them. */
export function countedFromThread(
  messages: readonly {
    direction: string;
    kind: string;
    status: string;
    msgClass: MessageClass | null;
    lastAttemptAt: string | null;
  }[],
  now: Date,
): CountedText[] {
  const since = now.getTime() - CAP_WINDOW_MS;
  const out: CountedText[] = [];
  for (const m of messages) {
    if (m.direction !== 'out' || REQUIRED.has(m.kind)) continue;
    const cls = m.msgClass ?? 'reply';
    if (COUNTED.has(m.status) && m.lastAttemptAt && new Date(m.lastAttemptAt).getTime() > since) {
      out.push({ at: new Date(m.lastAttemptAt), class: cls });
    } else if (m.status === 'queued') {
      out.push({ at: now, class: cls });
    }
  }
  return out;
}

/** "9:40 PM EDT" or "9:40 PM EDT · 8:40 PM CDT". */
export function localTimes(at: Date, zones: readonly string[]): string {
  return zones.map((z) => `${formatClock(at, z)} ${zoneAbbreviation(z, at)}`).join(' · ');
}

/** "Tue 8:02 AM EDT (5:02 AM yours)", or without the second part when the viewer is in that zone. */
export function theirTimeAndYours(at: Date, zones: readonly string[]): string {
  const theirs = formatInZones(at, zones);
  // Same clock for the viewer (same zone, or another zone with the same offset now): say it once.
  if (zones.length === 1 && formatDayTime(at, zones[0]) === formatDayTime(at)) return theirs;
  // The weekday again only when it differs for the viewer ("Mon 11:02 PM yours").
  const weekday = (zone?: string) => formatDayTime(at, zone).split(' ')[0];
  const yours = weekday(zones[0]) === weekday() ? formatClock(at) : formatDayTime(at);
  return `${theirs} (${yours} yours)`;
}

/** The composer's notice for one more reply to this merchant, or null when it would go out now. */
export function ComposeWindowNotice({
  decision,
  name,
  now,
}: {
  decision: ComplianceDecision;
  name: string;
  now: Date;
}) {
  let text: ReactNode;
  let tone: 'warning' | 'neutral' = 'warning';
  if (decision.ok) {
    const left = decision.closesAt.getTime() - now.getTime();
    if (left > 30 * 60_000) return null;
    tone = 'neutral';
    text = `Sending hours for ${name} end at ${theirTimeAndYours(decision.closesAt, decision.location.zones)}.`;
  } else if (decision.action === 'block') {
    return null; // The compose box is off (composeDisabledReason).
  } else if (decision.reason === 'timezone_unknown') {
    text = (
      <>
        We can&apos;t tell {name}&apos;s time zone from the number. Your text waits until you add
        their state on the profile.
      </>
    );
  } else if (decision.reason === 'no_window') {
    text = `The sending rules leave no time to text ${name} in the next two weeks. An admin can check Settings → Sending hours.`;
  } else {
    const zones = decision.location?.zones ?? [];
    const when = decision.until ? theirTimeAndYours(decision.until, zones) : 'later';
    if (decision.reason === 'state_cap' && decision.cap) {
      text = `${decision.cap.rule.label} allows ${decision.cap.rule.max} texts per 24 hours. Your text goes out ${when}.`;
    } else if (decision.reason === 'holiday') {
      text = `It's a federal holiday for ${name}. Your text goes out ${when}.`;
    } else {
      text = `It's ${localTimes(now, zones)} for ${name}. Your text goes out ${when}.`;
    }
  }
  return (
    <p
      role="status"
      data-testid="window-notice"
      className={`callout rounded-md px-3 py-2 text-ink ${tone === 'warning' ? 'bg-warning-tint' : 'bg-surface'}`}
    >
      {text}
    </p>
  );
}

/** Held reasons that need a person, so there is no time to show. */
const NEEDS_SOMEONE = new Set([
  'timezone_unknown',
  'rules_unavailable',
  'compliance_check_failed',
  'no_number',
  'number_paused',
]);

/** "Held until Tue 9:02 AM EDT (6:02 AM yours)", or "Held" when a person must fix something first. */
export function heldLabel(
  msg: { statusReason: string | null; notBefore: string | null },
  zones: readonly string[] | null,
): string {
  const base = (msg.statusReason ?? '').split(':')[0] ?? '';
  if (!msg.notBefore || NEEDS_SOMEONE.has(base)) return 'Held';
  const at = new Date(msg.notBefore);
  return `Held until ${zones && zones.length > 0 ? theirTimeAndYours(at, zones) : formatDayTime(at)}`;
}

function windowLine(decision: ComplianceDecision, zones: readonly string[]): string {
  if (decision.ok) return `Open until ${theirTimeAndYours(decision.closesAt, zones)}`;
  if (decision.action === 'block') return 'Blocked';
  if (decision.reason === 'state_cap' && decision.cap) {
    return `${decision.cap.rule.label} limit reached; next ${decision.until ? theirTimeAndYours(decision.until, zones) : 'later'}`;
  }
  return decision.until ? `Closed; opens ${theirTimeAndYours(decision.until, zones)}` : 'Closed';
}

function ruleSummary(t: {
  start: number | null;
  end: number | null;
  sundayAllowed: boolean;
  sundayStart: number | null;
}): string {
  const parts: string[] = [];
  if (t.start !== null || t.end !== null) {
    parts.push(
      `${t.start !== null ? formatTimeOfDay(t.start) : 'any time'}–${t.end !== null ? formatTimeOfDay(t.end) : 'any time'}`,
    );
  }
  if (t.sundayStart !== null) parts.push(`Sundays from ${formatTimeOfDay(t.sundayStart)}`);
  if (!t.sundayAllowed) parts.push('no Sundays');
  return parts.join(', ');
}

/**
 * The merchant profile's "Sending hours" card: local time, where the zone comes from, whether replies
 * and campaign texts can go out now, the rules that apply, and (admins) the manual time zone.
 */
export function MerchantSendingHours({
  workspaceId,
  merchant,
  admin,
}: {
  workspaceId: string;
  merchant: MerchantForRules & { id: string };
  admin: boolean;
}) {
  const rules = useRules(workspaceId);
  const now = useNow();
  const setZone = useSetMerchantTimezone(workspaceId, merchant.id);
  const [choice, setChoice] = useState<string | null>(null);

  if (rules.error) return <Alert tone="error">{rules.error.message}</Alert>;
  if (!rules.data || !rules.overrides) return <Spinner label="Loading the sending rules" />;
  const data = rules.data;
  const location = resolveLocation(
    {
      phone: merchant.phone,
      state: merchant.state,
      manualZone: merchant.timezoneSource === 'manual' ? merchant.timezone : null,
    },
    data,
  );

  if (!location.ok && location.action === 'block') {
    return (
      <p className="callout text-ink" data-testid="sending-hours">
        This number is outside the US. Fundline can&apos;t text it.
      </p>
    );
  }

  const candidates = location.ok ? location.candidates : [];
  const current = merchant.timezoneSource === 'manual' ? (merchant.timezone ?? '') : '';
  const selected = choice ?? current;
  const zoneSelect =
    admin && candidates.length > 1 ? (
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setZone.mutate(selected === '' ? null : selected, { onSuccess: () => setChoice(null) });
        }}
      >
        <div className="min-w-48 flex-1">
          <SelectField
            label="Time zone"
            value={selected}
            onChange={(e) => setChoice(e.target.value)}
            options={[
              { value: '', label: 'Automatic (from the state and area code)' },
              ...candidates.map((z) => ({ value: z, label: zoneName(z) })),
            ]}
            hint="Only the zones of the merchant's state and area code."
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          busy={setZone.isPending}
          disabled={selected === current}
        >
          Save time zone
        </Button>
        {setZone.error ? <Alert tone="error">{setZone.error.message}</Alert> : null}
      </form>
    ) : null;

  if (!location.ok) {
    return (
      <div className="flex flex-col gap-3" data-testid="sending-hours">
        <Alert tone="warning">
          We can&apos;t tell this merchant&apos;s time zone from the number. Add their state and
          texts go out in their sending hours.
        </Alert>
        {zoneSelect}
      </div>
    );
  }

  const assembled = assembleRules(location.states, data.stateRules, rules.overrides);
  const decide = (cls: MessageClass) =>
    previewDecision(merchant, cls, data, rules.overrides ?? [], [], now);
  const stateRows = location.states
    .map((s) => data.stateRules.get(s))
    .filter((r) => r !== undefined);

  return (
    <div className="flex flex-col gap-3" data-testid="sending-hours">
      <dl className="callout grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-ink-muted">Local time</dt>
        <dd className="text-ink" data-testid="merchant-local-time">
          {localTimes(now, location.zones)}
        </dd>
        <dt className="text-ink-muted">Time zone</dt>
        <dd className="text-ink">
          {location.zones.map(zoneName).join(' and ')}
          <span className="footnote block text-ink-muted">
            {describeLocationSource(location.source, location.mismatch)}
            {location.zones.length > 1
              ? '. Texts go out only when it is sending hours in both.'
              : ''}
          </span>
        </dd>
        <dt className="text-ink-muted">Replies</dt>
        <dd className="text-ink" data-testid="window-reply">
          {windowLine(decide('reply'), location.zones)}
        </dd>
        <dt className="text-ink-muted">Campaigns</dt>
        <dd className="text-ink" data-testid="window-campaign">
          {windowLine(decide('campaign'), location.zones)}
        </dd>
      </dl>
      {location.manualIgnored ? (
        <Alert tone="warning">
          The time zone an admin set no longer fits the state and area code, so the automatic one is
          used.
        </Alert>
      ) : null}
      {stateRows.some(
        (r) => r.allowedStart || r.allowedEnd || r.sundayStart || !r.sundayAllowed || r.maxPer24h,
      ) || assembled.tightenings.some((t) => t.label.startsWith('Workspace')) ? (
        <div className="flex flex-col gap-1">
          <p className="footnote font-semibold text-ink-muted">Stricter rules that apply</p>
          <ul className="flex flex-col gap-1">
            {assembled.tightenings.map((t, i) => {
              const summary = ruleSummary(t);
              const cap = assembled.caps.find((c) => c.label === t.label);
              const row = stateRows.find((r) => r.name === t.label);
              if (!summary && !cap) return null;
              return (
                <li
                  key={`${t.label}-${i}`}
                  className="callout flex flex-wrap items-center gap-2 text-ink"
                >
                  <span>
                    {t.label}:{' '}
                    {[
                      summary,
                      cap
                        ? `${cap.max} texts per 24 hours${cap.appliesTo.length === 1 ? ' (campaigns and sequences)' : ''}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {row && !row.verifiedByCounsel ? (
                    <Badge role="pending">Not verified by counsel</Badge>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {zoneSelect}
    </div>
  );
}

/** The conversation header's "· 2:14 PM EDT" (nothing while the rules load or for numbers we can't place). */
export function useMerchantLocalTime(merchant: MerchantForRules | null): {
  zones: string[] | null;
  local: string | null;
} {
  const reference = useComplianceReferenceQuery();
  const now = useNow();
  if (!merchant || !reference.data) return { zones: null, local: null };
  const location = resolveLocation(
    {
      phone: merchant.phone,
      state: merchant.state,
      manualZone: merchant.timezoneSource === 'manual' ? merchant.timezone : null,
    },
    reference.data,
  );
  if (!location.ok) return { zones: null, local: null };
  return { zones: location.zones, local: localTimes(now, location.zones) };
}
